import { ReactNode } from 'react';
import { Connection } from "@domain/connection/models/Connection";
import { BolConnectionListItem } from "../../bol/components/BolConnectionListItem";
import { ListItem, ListItemText, Avatar, useTheme, ListItemButton } from "@mui/material";
import { APP_CONFIG } from "../../../../config/app.config";
import { ProductFlowConnectionListItem } from "../../product_flow/components/ProductFlowConnectionListItem";
import { WooCommerceConnectionListItem } from "@features/connections/woocommerce/components/WooCommerceConnectionListItem";
import { ShopifyConnectionListItem } from "@features/connections/shopify/components/ShopifyConnectionListItem";
import { AmazonConnectionListItem } from "@features/connections/amazon/components/AmazonConnectionListItem";
import { MontaConnectionListItem } from "@features/connections/monta/components/MontaConnectionListItem";
import { ChannelDockConnectionListItem } from "@features/connections/channeldock/components/ChannelDockConnectionListItem";
import { ConnectionAuthStatus } from "@features/connections/generic/components/ConnectionAuthStatus";
import { Icon } from "@shared/icons";
import GenericConnectionSettings from "./GenericConnectionSettings";

export type ConnectionSettingsClickHandler = (settings: ReactNode) => void;

export interface ConnectionListItemProps {
    connection: Connection;
    /** When set, the row is clickable and shows a chevron. Receives the connector's settings component. */
    onClick?: ConnectionSettingsClickHandler;
}

export const ConnectionListItem = ({ connection, onClick }: ConnectionListItemProps) => {
    const theme = useTheme();

    if (connection.connector.type === 'bol') {
        return <BolConnectionListItem connection={connection} onClick={onClick} />;
    }
    if (connection.connector.type === 'product_flow') {
        return <ProductFlowConnectionListItem connection={connection} onClick={onClick} />;
    }
    if (connection.connector.type === 'woocommerce') {
        return <WooCommerceConnectionListItem connection={connection} onClick={onClick} />;
    }
    if (connection.connector.type === 'shopify') {
        return <ShopifyConnectionListItem connection={connection} onClick={onClick} />;
    }
    if (connection.connector.type === 'amazon') {
        return <AmazonConnectionListItem connection={connection} onClick={onClick} />;
    }
    if (connection.connector.type === 'monta') {
        return <MontaConnectionListItem connection={connection} onClick={onClick} />;
    }
    if (connection.connector.type === 'channeldock') {
        return <ChannelDockConnectionListItem connection={connection} onClick={onClick} />;
    }

    const handleClick = onClick
        ? () => onClick(<GenericConnectionSettings connectionId={connection.id} />)
        : undefined;

    const content = (
        <>
            <Avatar
                variant="rounded"
                sx={{ width: 60, height: 60, border: 1, borderColor: APP_CONFIG.ui.theme.colors.border, backgroundColor: theme.palette.background.paper }}
                src={connection.connector.image}
                alt={connection.connector.type}
                slotProps={{
                    img: {
                        style: { objectFit: 'contain' }
                    }
                }}
            />

            <ListItemText primary={connection.name} secondary={connection.connector.type} />

            <ConnectionAuthStatus authorized={connection.authorized} onReconnect={() => { }} />

            {onClick && (
                <Icon icon="ChevronRight" color={theme.palette.text.secondary} />
            )}
        </>
    );

    if (handleClick) {
        return (
            <ListItemButton sx={{ p: 2, gap: 2 }} onClick={handleClick}>
                {content}
            </ListItemButton>
        );
    }

    return (
        <ListItem sx={{ p: 2, gap: 2 }}>
            {content}
        </ListItem>
    );
};
